import React, { useCallback, useEffect, useState } from 'react';
import { CalendarOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Layout, Modal, PageHeader, Select, Space, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage } from '../../utils';
import { Location, Smat, User } from '../../models';
import { SmatPlanEditPage } from './smat-plan-edit-page';
import { SmatAuditPage } from '../smats/smat-audit-page';
import { useForm } from 'antd/lib/form/Form';

export function SmatPlansPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [loading, setLoading] = useState(false);
  const [smatCalendar, setSmatCalendar] = useState<any[]>();
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [plansModalUserId, setPlansModalUserId] = useState<string>();
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditModalSmatId, setAuditModalSmatId] = useState<string>();
  const [tableColumns, setTableColumns] = useState<any>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<any>(undefined);

  const loadSmatPlans = useCallback(() => {
    setLoading(true);

    const queryParams = [`year=2023`, `scope=all`];
    if (filterValues) {
      if (filterValues.locationId && filterValues.locationId !== 'all') {
        queryParams.push(`locationId=${filterValues.locationId}`);
      }
      if (filterValues.status && filterValues.status !== 'all') {
        queryParams.push(`status=${filterValues.status}`);
      }
      if (filterValues.userId && filterValues.userId !== 'all') {
        queryParams.push(`userId=${filterValues.userId}`);
      }
      if (filterValues.week && filterValues.week !== 'all') {
        queryParams.push(`week=${filterValues.week}`);
      }
    }
    const queryParamsStr = queryParams.join('&');

    apiClient
      .get(`smats?${queryParamsStr}`)
      .then(response => {
        const responseSmats = response.data as Smat[];
        let responseSmatCalendar: any[] = [];
        for (let i = 0; i < responseSmats.length; i++) {
          const user = responseSmats[i].user;
          if (!responseSmatCalendar.some(e => e.user.id === user.id)) {
            const calendarItem: any = { user, key: user.id };
            for (let y = 1; y <= 52; y++) {
              const weekSmats = responseSmats.filter(s => s.user.id === user.id && s.week === y);
              calendarItem[y] = weekSmats;
            }
            responseSmatCalendar.push(calendarItem);
          }
        }
        setSmatCalendar(responseSmatCalendar);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, [filterValues]);

  useEffect(() => {
    loadSmatPlans();
  }, [loadSmatPlans]);

  useEffect(() => {
    const newTableColumns: any[] = [
      {
        title: 'Personel',
        dataIndex: 'user',
        key: 'user',
        render: (user: User) => (
          <Button type="link" onClick={() => openPlanModalById(user.id)}>
            {user.fullNameId}
          </Button>
        ),
      },
    ];

    let weekFrom = 1;
    let weekTo = 52;

    if (filterValues && filterValues.week && filterValues.week !== 'all') {
      weekFrom = filterValues.week;
      weekTo = filterValues.week;
    }

    for (let i = weekFrom; i <= weekTo; i++) {
      const tableColumn = {
        title: `${i}`,
        dataIndex: `${i}`,
        key: `${i}`,
        render: (weekSmats: Smat[]) => (
          <Space direction="vertical">
            {weekSmats?.map((s: Smat) => (
              <Button type="text" size="small" onClick={() => openAuditModal(s.id)}>
                <Text
                  type={s?.status === 2 ? 'success' : s?.status === 3 ? 'warning' : s?.status === 4 ? 'danger' : undefined}
                  style={{ fontSize: '0.8rem' }}
                >
                  {s?.area.code}
                </Text>
              </Button>
            ))}
          </Space>
        ),
      };
      newTableColumns.push(tableColumn);
    }

    setTableColumns(newTableColumns);
  }, [Text, filterValues]);

  const onRefreshClick = () => {
    loadSmatPlans();
  };

  const onFiltersToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const onFiltersChange = (filterValues: any) => {
    setFilterValues(filterValues);
  };

  const onPlanChanged = () => {
    closePlanModal();
    loadSmatPlans();
  };

  const onSmatChanged = () => {
    closePlanModal();
    loadSmatPlans();
  };

  const openEditModal = () => {
    setPlansModalUserId(undefined);
    setPlansModalOpen(true);
  };

  const openPlanModalById = (userId: string) => {
    setPlansModalUserId(userId);
    setPlansModalOpen(true);
  };

  const closePlanModal = () => {
    setPlansModalOpen(false);
    setPlansModalUserId(undefined);
  };

  const openAuditModal = (smatId: string) => {
    setAuditModalSmatId(smatId);
    setAuditModalOpen(true);
  };

  const closeAuditModal = () => {
    setAuditModalOpen(false);
    setAuditModalSmatId(undefined);
  };

  return (
    <>
      <PageHeader
        title={
          <>
            <CalendarOutlined /> SMAT Planları
          </>
        }
        extra={[
          <Button key="filters" onClick={onFiltersToggle} icon={<FilterOutlined />}>
            Filtreleri {filtersOpen ? 'gizle' : 'göster'}
          </Button>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openEditModal} icon={<CalendarOutlined />} type="primary">
            SMAT planı düzenle
          </Button>,
        ]}
      />
      <Content>
        <FiltersForm hidden={!filtersOpen} onChange={onFiltersChange} />
        <Table columns={tableColumns} dataSource={smatCalendar} loading={loading} pagination={false} bordered scroll={{ x: '100%' }} />
      </Content>
      <Modal title="Yeni SMAT planı" visible={plansModalOpen} footer={null} onCancel={closePlanModal} destroyOnClose={true}>
        <SmatPlanEditPage userId={plansModalUserId} onSaved={onPlanChanged} />
      </Modal>
      <Modal title="SMAT denetleme" visible={auditModalOpen} footer={null} onCancel={closeAuditModal} destroyOnClose={true} width="90%">
        <SmatAuditPage smatId={auditModalSmatId} onSaved={onSmatChanged} />
      </Modal>
    </>
  );
}

function FiltersForm(props: { hidden: boolean; onChange: (filterValues: any) => void }) {
  const [locations, setLocations] = useState<Location[]>();
  const [users, setUsers] = useState<User[]>();
  const [weeks, setWeeks] = useState<number[]>();
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const { Option } = Select;
  const [form] = useForm();

  useEffect(() => {
    loadLocations();
    loadUsers();

    const newWeeks = [];
    for (let i = 1; i <= 52; i++) {
      newWeeks.push(i);
    }
    setWeeks(newWeeks);
  }, []);

  const loadLocations = () => {
    setLocationsLoading(true);
    apiClient
      .get('smats/locations')
      .then(response => {
        setLocations(response.data);
        setLocationsLoading(false);
      })
      .catch((reason: any) => {
        setLocationsLoading(false);
        apiErrorMessage(reason);
      });
  };

  const loadUsers = () => {
    setLocationsLoading(true);
    apiClient
      .get('smats/users')
      .then(response => {
        setUsers(response.data);
        setUsersLoading(false);
      })
      .catch((reason: any) => {
        setUsersLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onChange = () => {
    const values = form.getFieldsValue();
    props.onChange(values);
  };

  if (props.hidden) {
    return null;
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <Form form={form} layout="inline">
        <Form.Item name="locationId">
          <Select allowClear placeholder="Lokasyon" style={{ width: '12rem' }} loading={locationsLoading} onChange={onChange}>
            <Option key="area_all" value="all">
              Tümü
            </Option>
            {locations?.map(l => (
              <Option key={l.id} value={l.id}>
                {l.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="status">
          <Select
            allowClear
            showSearch
            placeholder="Statü"
            style={{ width: '12rem' }}
            onChange={onChange}
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            <Option value="all">Tümü</Option>
            <Option value="0">Planlandı</Option>
            <Option value="1">Aktif</Option>
            <Option value="2-3">Tamamlandı</Option>
            <Option value="2">Tamamlandı (Zamanında)</Option>
            <Option value="3">Tamamlandı (Geç)</Option>
            <Option value="4">Tamamlanmadı</Option>
          </Select>
        </Form.Item>
        <Form.Item name="userId">
          <Select
            allowClear
            showSearch
            placeholder="Denetleyici"
            style={{ width: '12rem' }}
            loading={usersLoading}
            onChange={onChange}
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            <Option key="users_all" value="all">
              Tümü
            </Option>
            {users?.map(l => (
              <Option key={l.id} value={l.id}>
                {l.fullNameId}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="week">
          <Select
            allowClear
            showSearch
            placeholder="Hafta"
            style={{ width: '12rem' }}
            onChange={onChange}
            filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          >
            <Option key="weeks_all" value="all">
              Tümü
            </Option>
            {weeks?.map(w => (
              <Option key={w} value={w}>
                {w}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Card>
  );
}
